import { eachDayOfInterval, format, parseISO, subMilliseconds } from 'date-fns'
import last from 'lodash-es/last'

import {
  CALENDAR_GRID_LABEL_SEPARATOR,
  CalendarGridItemVariant,
} from '../../../../constants/calendarGrid'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../../constants/date'
import { toUkTimezoneDate } from '../../../../utils/date'
import { getCalendarGridDayTime } from '../../../CalendarGrid'
import {
  CALENDAR_APPOINTMENT_CONTINUATION_MARKER,
  CALENDAR_ITEM_KIND_VARIANT,
} from '../../Calendar.constants'
import { getIsCalendarAppointmentCancelled } from '../../Calendar.utils'

function getCalendarAppointmentAnchorLabel(anchor) {
  const anchorName = anchor.request?.number ?? anchor.building?.short_name

  return anchor.flat
    ? [anchorName, anchor.flat.name].join(CALENDAR_GRID_LABEL_SEPARATOR)
    : anchorName
}

function getCalendarAppointmentHint({ anchor, isCancelled, t }) {
  if (isCancelled) {
    return t('Calendar.AppointmentCancelledHintKey')
  }

  return anchor.is_archived
    ? t('Calendar.AppointmentArchivedHintKey', {
        anchor: getCalendarAppointmentAnchorLabel(anchor),
      })
    : null
}

function getCalendarLocalDate({ timezone, value }) {
  return format(toUkTimezoneDate(value, timezone), DATE_API_DATA_FORMAT_SHORT)
}

// The span is [start_at, end_at): an all-day one ends at 00:00 of the day
// after its last date (BR-CAL-02-02)
function getCalendarAppointmentDates({ end, start, timezone }) {
  return eachDayOfInterval({
    end: parseISO(
      getCalendarLocalDate({
        timezone,
        value: subMilliseconds(parseISO(end), 1),
      })
    ),
    start: parseISO(getCalendarLocalDate({ timezone, value: start })),
  }).map(day => format(day, DATE_API_DATA_FORMAT_SHORT))
}

function getCalendarAppointmentDayStart({ date, timezone }) {
  return getCalendarGridDayTime({
    day: date,
    minutes: 0,
    timezone,
  }).toISOString()
}

function toCalendarGridAppointment({
  dates,
  item: {
    anchor,
    end_at: end,
    id,
    is_all_day: isAllDay,
    kind,
    start_at: start,
    status,
    title,
  },
  t,
}) {
  const [date] = dates

  const isCancelled = getIsCalendarAppointmentCancelled(status)

  return {
    date,
    end,
    hint: getCalendarAppointmentHint({ anchor, isCancelled, t }),
    id,
    isAllDay,
    isArchived: anchor.is_archived,
    isCancelled,
    isStatusPinned: true,
    kind,
    lastDate: last(dates),
    secondaryLabel: getCalendarAppointmentAnchorLabel(anchor),
    start,
    title,
    url: anchor.request?.url ?? anchor.building?.url,
    variant:
      isCancelled || anchor.is_archived
        ? CalendarGridItemVariant.muted
        : CALENDAR_ITEM_KIND_VARIANT[kind],
  }
}

function toCalendarGridAppointmentDay({
  appointment,
  date,
  index,
  nextDate,
  timezone,
}) {
  const isContinued = index > 0

  return {
    ...appointment,
    date,
    end: nextDate
      ? getCalendarAppointmentDayStart({ date: nextDate, timezone })
      : appointment.end,
    isContinued,
    lastDate: date,
    start: isContinued
      ? getCalendarAppointmentDayStart({ date, timezone })
      : appointment.start,
    title: isContinued
      ? `${CALENDAR_APPOINTMENT_CONTINUATION_MARKER} ${appointment.title}`
      : appointment.title,
  }
}

// TODO: open the appointment detail panel on click once it exists, instead of
// the grid card.
export function toCalendarGridAppointmentDays({ item, t, timezone }) {
  const dates = getCalendarAppointmentDates({
    end: item.end_at,
    start: item.start_at,
    timezone,
  })

  const appointment = toCalendarGridAppointment({ dates, item, t })

  return appointment.isAllDay
    ? [appointment]
    : dates.map((date, index) =>
        toCalendarGridAppointmentDay({
          appointment,
          date,
          index,
          nextDate: dates[index + 1],
          timezone,
        })
      )
}
